import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import {
  MagnifyingGlassIcon,
  ClockIcon,
  EllipsisVerticalIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  BuildingOfficeIcon,
  CheckIcon,
  ArrowsUpDownIcon,
  XMarkIcon,
  TrashIcon,
  UserCircleIcon,
  ArrowsRightLeftIcon,
} from '@heroicons/react/24/outline';
import { usePageTitle } from '../hooks/usePageTitle';
import apiService, { User, UserFilters } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { validatePassword, PasswordPolicy, PasswordValidationResult } from '../utils/passwordValidation';

type SortField = 'email' | 'role' | 'company' | 'status' | 'created_at' | 'last_login' | 'file_count';
type SortOrder = 'asc' | 'desc';

const Users: React.FC = () => {
  const { t: tUsers } = useTranslation('users');
  const { t } = useTranslation('forgotPassword'); // Use forgotPassword namespace for password validation messages
  usePageTitle(tUsers('title'), tUsers('subtitle'));
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const isSysAdmin = currentUser?.role === 'sysadmin';
  
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [sortField, setSortField] = useState<SortField>('created_at');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [openUserMenu, setOpenUserMenu] = useState<string | null>(null);
  const [groupByCompany, setGroupByCompany] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [isActivating, setIsActivating] = useState(false);
  const [roleDropdownOpen, setRoleDropdownOpen] = useState(false);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [sortDropdownOpen, setSortDropdownOpen] = useState(false);
  const [showChangeRoleModal, setShowChangeRoleModal] = useState(false);
  const [showChangeCompanyModal, setShowChangeCompanyModal] = useState(false);
  const [showBulkOwnershipTransferModal, setShowBulkOwnershipTransferModal] = useState(false);
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [createUserFormKey, setCreateUserFormKey] = useState(0);
  const [isChangingRole, setIsChangingRole] = useState(false);
  const [isChangingCompany, setIsChangingCompany] = useState(false);
  const [isBulkUpdatingOwnershipTransfer, setIsBulkUpdatingOwnershipTransfer] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeletingUsers, setIsDeletingUsers] = useState(false);
  const [bulkDeleteProgress, setBulkDeleteProgress] = useState<{ current: number; total: number } | null>(null);
  const [isCreatingUser, setIsCreatingUser] = useState(false);
  const [companies, setCompanies] = useState<any[]>([]);
  const [createUserCompaniesWithLicenses, setCreateUserCompaniesWithLicenses] = useState<Array<{ id: string; name: string; full_available: number; reader_available: number }>>([]);
  const roleDropdownRef = React.useRef<HTMLDivElement>(null);
  const statusDropdownRef = React.useRef<HTMLDivElement>(null);
  const sortDropdownRef = React.useRef<HTMLDivElement>(null);
  
  // Create user form state
  const [createUserForm, setCreateUserForm] = useState({
    email: '',
    name: '',
    password: '',
    passwordConfirm: '',
    role: 'user' as 'user' | 'admin' | 'sysadmin' | 'translator',
    translatorLanguage: '' as '' | 'en' | 'de' | 'es' | 'fr' | 'fi' | 'tr',
    company_id: '',
    mode: 'full' as 'full' | 'reader',
  });
  
  // Password validation state
  const [passwordPolicy, setPasswordPolicy] = useState<PasswordPolicy | null>(null);
  const [passwordValidation, setPasswordValidation] = useState<PasswordValidationResult | null>(null);
  const [isLoadingPasswordPolicy, setIsLoadingPasswordPolicy] = useState(false);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const filters: UserFilters = {
        page: 1,
        // No limit - fetch all users
      };
      
      if (searchTerm) filters.search = searchTerm;
      // Don't send role/status filters to backend - we'll filter on frontend for multi-select support

      const response = await apiService.getUsers(filters);
      setUsers(response.users || []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [searchTerm]);

  // Fetch users from API
  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openUserMenu) {
        setOpenUserMenu(null);
      }
      // Close role dropdown
      if (roleDropdownRef.current && !roleDropdownRef.current.contains(event.target as Node)) {
        setRoleDropdownOpen(false);
      }
      // Close status dropdown
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target as Node)) {
        setStatusDropdownOpen(false);
      }
      // Close sort dropdown
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(event.target as Node)) {
        setSortDropdownOpen(false);
      }
    };
    if (openUserMenu || roleDropdownOpen || statusDropdownOpen || sortDropdownOpen) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [openUserMenu, roleDropdownOpen, statusDropdownOpen, sortDropdownOpen]);

  // Listen for custom event from Header button to open create user modal
  useEffect(() => {
    const handleOpenModal = () => {
      // Reset form when opening modal
      setCreateUserForm({
        email: '',
        name: '',
        password: '',
        passwordConfirm: '',
        role: 'user',
        translatorLanguage: '',
        company_id: '',
        mode: 'full',
      });
      setPasswordPolicy(null);
      setPasswordValidation(null);
      // Force form reset by changing key
      setCreateUserFormKey(prev => prev + 1);
      setShowCreateUserModal(true);
    };
    
    window.addEventListener('open-create-user-modal', handleOpenModal);
    return () => {
      window.removeEventListener('open-create-user-modal', handleOpenModal);
    };
  }, []);
  
  // Reset form when modal closes
  useEffect(() => {
    if (!showCreateUserModal) {
      // Reset form when modal closes
      setCreateUserForm({
        email: '',
        name: '',
        password: '',
        passwordConfirm: '',
        role: 'user',
        translatorLanguage: '',
        company_id: '',
        mode: 'full',
      });
      // Reset password validation
      setPasswordPolicy(null);
      setPasswordValidation(null);
      // Force form reset by changing key
      setCreateUserFormKey(prev => prev + 1);
    }
  }, [showCreateUserModal]);

  // Fetch companies with available licenses when create-user modal opens (sysadmin dropdown: only companies with license room)
  useEffect(() => {
    if (showCreateUserModal && isSysAdmin) {
      apiService.getCompaniesWithAvailableLicenses()
        .then((list) => setCreateUserCompaniesWithLicenses(list))
        .catch(() => setCreateUserCompaniesWithLicenses([]));
    } else {
      setCreateUserCompaniesWithLicenses([]);
    }
  }, [showCreateUserModal, isSysAdmin]);

  // When company selection changes and only one license type is available, set mode to that and keep in sync
  const selectedCompanyAvailability = createUserForm.company_id
    ? createUserCompaniesWithLicenses.find((c) => c.id === createUserForm.company_id)
    : null;
  const companyHasBothModes = selectedCompanyAvailability
    ? selectedCompanyAvailability.full_available > 0 && selectedCompanyAvailability.reader_available > 0
    : false;
  const companyOnlyFull = selectedCompanyAvailability
    ? selectedCompanyAvailability.full_available > 0 && selectedCompanyAvailability.reader_available <= 0
    : false;
  const companyOnlyReader = selectedCompanyAvailability
    ? selectedCompanyAvailability.reader_available > 0 && selectedCompanyAvailability.full_available <= 0
    : false;
  useEffect(() => {
    if (selectedCompanyAvailability && !companyHasBothModes) {
      const forcedMode = companyOnlyReader ? 'reader' : 'full';
      setCreateUserForm((prev) => (prev.mode === forcedMode ? prev : { ...prev, mode: forcedMode as 'full' | 'reader' }));
    }
    // Primitives only; selectedCompanyAvailability object reference changes every render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createUserForm.company_id, selectedCompanyAvailability?.id, selectedCompanyAvailability?.full_available, selectedCompanyAvailability?.reader_available, companyHasBothModes, companyOnlyFull, companyOnlyReader]);

  // Fetch password policy when company is selected
  useEffect(() => {
    const fetchPasswordPolicy = async () => {
      if (createUserForm.company_id) {
        setIsLoadingPasswordPolicy(true);
        try {
          const policy = await apiService.getCompanyPasswordSettings(createUserForm.company_id);
          setPasswordPolicy(policy);
        } catch (error) {
          console.error('Failed to fetch password policy:', error);
          setPasswordPolicy(null);
        } finally {
          setIsLoadingPasswordPolicy(false);
        }
      } else {
        setPasswordPolicy(null);
        setPasswordValidation(null);
      }
    };
    
    fetchPasswordPolicy();
  }, [createUserForm.company_id]);
  
  // Validate password when password or policy changes
  useEffect(() => {
    if (createUserForm.password) {
      // Use company policy if available, otherwise use default (min 8 chars)
      const policyToUse: PasswordPolicy = passwordPolicy || {
        min_length: 8,
        require_uppercase: false,
        require_lowercase: false,
        require_numbers: false,
        require_special_chars: false,
        prohibit_sequential_numbers: false,
        max_sequential_numbers: 0,
        prohibit_repeated_characters: false,
        max_repeated_characters: 0,
        prohibit_common_passwords: false,
        prohibit_user_info: false,
        prohibit_easy_to_guess: false,
        require_password_rotation: false,
        password_rotation_days: 0,
        password_expiration_notification_days: 0,
      };
      
      const validation = validatePassword(
        createUserForm.password,
        policyToUse,
        createUserForm.email,
        createUserForm.name,
        t
      );
      setPasswordValidation(validation);
    } else {
      setPasswordValidation(null);
    }
  }, [createUserForm.password, createUserForm.email, createUserForm.name, passwordPolicy, t]);

  // Helper functions (defined before sortedUsers memo)
  const getStatusText = (user: User) => {
    // Status logic:
    // - active: is_active = true AND is_verified = true
    // - pending-verified: is_active = false AND is_verified = true (verified but waiting for license)
    // - pending-unverified: is_active = false AND is_verified = false (not verified yet)
    if (!user.is_active && !user.is_verified) return 'pending-unverified';
    if (!user.is_active && user.is_verified) return 'pending-verified';
    return 'active';
  };

  // Client-side filtering and sorting
  const sortedUsers = React.useMemo(() => {
    // First, apply multi-select filters
    let filtered = [...users];
    
    // Filter by multiple roles
    if (selectedRoles.length > 0) {
      filtered = filtered.filter(user => selectedRoles.includes(user.role));
    }
    
    // Filter by multiple statuses
    if (selectedStatuses.length > 0) {
      filtered = filtered.filter(user => {
        const userStatus = getStatusText(user);
        return selectedStatuses.includes(userStatus);
      });
    }
    
    // Then sort
    const sorted = filtered.sort((a, b) => {
      let aVal: any = '';
      let bVal: any = '';

      switch (sortField) {
        case 'email':
          aVal = a.email.toLowerCase();
          bVal = b.email.toLowerCase();
          break;
        case 'role':
          aVal = a.role;
          bVal = b.role;
          break;
        case 'company':
          aVal = (a.company_name || '').toLowerCase();
          bVal = (b.company_name || '').toLowerCase();
          break;
        case 'status':
          aVal = getStatusText(a);
          bVal = getStatusText(b);
          break;
        case 'created_at':
          aVal = new Date(a.created_at).getTime();
          bVal = new Date(b.created_at).getTime();
          break;
        case 'last_login':
          aVal = a.last_login ? new Date(a.last_login).getTime() : 0;
          bVal = b.last_login ? new Date(b.last_login).getTime() : 0;
          break;
        case 'file_count':
          aVal = a.file_count ?? 0;
          bVal = b.file_count ?? 0;
          break;
      }

      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [users, sortField, sortOrder, selectedRoles, selectedStatuses]);

  // Group users by company
  const groupedUsers = React.useMemo(() => {
    if (!groupByCompany) {
      return { ungrouped: sortedUsers };
    }

    const groups: Record<string, { companyName: string; users: User[] }> = {};
    
    sortedUsers.forEach(user => {
      const companyId = user.company_id || 'ungrouped';
      const companyName = user.company_name || tUsers('noClient');
      
      if (!groups[companyId]) {
        groups[companyId] = { companyName, users: [] };
      }
      groups[companyId].users.push(user);
    });

    return groups;
  }, [sortedUsers, groupByCompany, tUsers]);

  // Expand all groups by default when grouping is enabled or users change
  useEffect(() => {
    if (groupByCompany && Object.keys(groupedUsers).length > 0) {
      setExpandedGroups(new Set(Object.keys(groupedUsers)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupByCompany, users.length]);

  const toggleGroup = (groupId: string) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupId)) {
        newSet.delete(groupId);
      } else {
        newSet.add(groupId);
      }
      return newSet;
    });
  };


  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'sysadmin':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
      case 'admin':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300';
      case 'user':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    // Check if it's January 1, year 1 (default zero time)
    if (date.getFullYear() === 1) return 'Never';
    
    // Format as YYYY-MM-DD HH:MM:SS
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  const handleUserClick = (user: User) => {
    navigate(`/users/${user.id}`, { state: { editMode: true } });
  };

  const handleViewActivity = (user: User) => {
    navigate(`/users/${user.id}/activity`);
  };

  const handleViewUserAssets = (e: React.MouseEvent, user: User) => {
    e.stopPropagation(); // Prevent card click
    navigate(`/assets?emailSearch=${encodeURIComponent(user.email)}`);
  };

  // Get display name (use email prefix if no name)
  const getDisplayName = (user: User) => {
    if (user.name) return user.name;
    const emailPrefix = user.email.split('@')[0];
    return emailPrefix;
  };

  // Selection handlers
  const handleSelectUser = (userId: string) => {
    setSelectedUsers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  };


  const handleActivateSelected = async () => {
    if (selectedUsers.size === 0) {
      toast.error('Please select at least one user to activate');
      return;
    }

    const selectedUserIds = Array.from(selectedUsers);
    const selectedUserList = sortedUsers.filter(u => selectedUserIds.includes(u.id));
    const pendingVerifiedSelected = selectedUserList.filter(u => !u.is_active && u.is_verified);

    if (pendingVerifiedSelected.length === 0) {
      toast.error('Selected users are already active or not verified');
      return;
    }

    setIsActivating(true);
    try {
      // Activate users one by one (manual activation - keeps available_licenses unchanged)
      let successCount = 0;
      let failCount = 0;

      for (const user of pendingVerifiedSelected) {
        try {
          await apiService.updateUser(user.id, { is_active: true });
          successCount++;
        } catch (error: any) {
          console.error(`Failed to activate user ${user.email}:`, error);
          failCount++;
        }
      }

      if (successCount > 0) {
        toast.success(`Successfully activated ${successCount} user${successCount !== 1 ? 's' : ''}`);
      }
      if (failCount > 0) {
        toast.error(`Failed to activate ${failCount} user${failCount !== 1 ? 's' : ''}`);
      }

      // Refresh users list
      await fetchUsers();
      setSelectedUsers(new Set());
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to activate users');
    } finally {
      setIsActivating(false);
    }
  };

  // Check if any selected users are pending-verified (for showing activate button)
  const selectedUserIds = Array.from(selectedUsers);
  const selectedUserList = sortedUsers.filter(u => selectedUserIds.includes(u.id));
  const hasPendingVerifiedSelected = selectedUserList.some(u => !u.is_active && u.is_verified);
  
  // Fetch companies for company admin
  useEffect(() => {
    const fetchCompanies = async () => {
      if (currentUser?.role === 'admin' || currentUser?.role === 'sysadmin') {
        try {
          const companiesList = await apiService.getCompanies();
          setCompanies(Array.isArray(companiesList) ? companiesList : []);
        } catch (error) {
          console.error('Failed to fetch companies:', error);
        }
      }
    };
    fetchCompanies();
  }, [currentUser]);
  
  // Get managed companies for company admin (backend returns only managed companies for admin; ensure array)
  const managedCompanies = React.useMemo(() => {
    if (currentUser?.role !== 'admin' || !currentUser.company_id) {
      return [];
    }
    const list = Array.isArray(companies) ? companies : [];
    // Company admin: backend returns only companies where managed_by = their company_id (own org edited via Settings)
    return list.filter(c =>
      c.id === currentUser.company_id || c.managed_by === currentUser.company_id
    );
  }, [companies, currentUser]);
  
  const hasManagedCompanies = managedCompanies.length > 0;
  
  // Bulk change role handler
  const handleBulkChangeRole = async (newRole: 'user' | 'admin' | 'sysadmin') => {
    if (selectedUsers.size === 0) {
      toast.error('Please select at least one user');
      return;
    }
    
    // Prevent admins from setting sysadmin role
    if (currentUser?.role === 'admin' && newRole === 'sysadmin') {
      toast.error('You cannot assign sysadmin role');
      return;
    }
    
    setIsChangingRole(true);
    try {
      let successCount = 0;
      let failCount = 0;
      
      for (const userId of selectedUserIds) {
        try {
          await apiService.updateUser(userId, { role: newRole });
          successCount++;
        } catch (error: any) {
          console.error(`Failed to update role for user ${userId}:`, error);
          failCount++;
        }
      }
      
      if (successCount > 0) {
        toast.success(`Successfully updated role for ${successCount} user${successCount !== 1 ? 's' : ''}`);
      }
      if (failCount > 0) {
        toast.error(`Failed to update role for ${failCount} user${failCount !== 1 ? 's' : ''}`);
      }
      
      await fetchUsers();
      setSelectedUsers(new Set());
      setShowChangeRoleModal(false);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to change roles');
    } finally {
      setIsChangingRole(false);
    }
  };
  
  // Bulk change company handler
  const handleBulkChangeCompany = async (companyId: string | null) => {
    if (selectedUsers.size === 0) {
      toast.error('Please select at least one user');
      return;
    }
    
    setIsChangingCompany(true);
    try {
      let successCount = 0;
      let failCount = 0;
      
      const payload = { company_id: companyId ?? null }; // Explicitly send null instead of undefined
      console.log('🔧 USERS PAGE: Bulk updating company for users', {
        userCount: selectedUserIds.length,
        userIds: Array.from(selectedUserIds),
        companyId: companyId,
        payload: payload,
        payloadStringified: JSON.stringify(payload),
        companyIdType: typeof companyId,
        isNull: companyId === null,
        isUndefined: companyId === undefined,
      });
      
      for (const userId of selectedUserIds) {
        try {
          console.log('🔧 USERS PAGE: Updating company for user', {
            userId: userId,
            companyId: companyId,
            payload: payload,
          });
          
          await apiService.updateUser(userId, payload);
          
          console.log('✅ USERS PAGE: Successfully updated company for user', {
            userId: userId,
            companyId: companyId,
          });
          
          successCount++;
        } catch (error: any) {
          console.error(`❌ USERS PAGE: Failed to update company for user ${userId}:`, {
            userId: userId,
            companyId: companyId,
            error: error,
            errorMessage: error?.message,
            errorResponse: error?.response?.data,
          });
          failCount++;
        }
      }
      
      if (successCount > 0) {
        toast.success(`Successfully updated company for ${successCount} user${successCount !== 1 ? 's' : ''}`);
      }
      if (failCount > 0) {
        toast.error(`Failed to update company for ${failCount} user${failCount !== 1 ? 's' : ''}`);
      }
      
      await fetchUsers();
      setSelectedUsers(new Set());
      setShowChangeCompanyModal(false);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to change companies');
    } finally {
      setIsChangingCompany(false);
    }
  };

  // Bulk update ownership transfer enabled handler
  const handleBulkUpdateOwnershipTransfer = async (enabled: boolean) => {
    if (selectedUsers.size === 0) {
      toast.error('Please select at least one user');
      return;
    }
    
    setIsBulkUpdatingOwnershipTransfer(true);
    try {
      const selectedUserIds = Array.from(selectedUsers);
      const result = await apiService.bulkUpdateUserPreferences(selectedUserIds, enabled);
      
      if (result.success_count > 0) {
        toast.success(`Successfully updated ownership transfer setting for ${result.success_count} user${result.success_count !== 1 ? 's' : ''}`);
      }
      if (result.error_count > 0) {
        const errorMessages = result.errors?.slice(0, 3).join(', ') || '';
        toast.error(`Failed to update ${result.error_count} user${result.error_count !== 1 ? 's' : ''}${errorMessages ? ': ' + errorMessages : ''}`);
      }
      
      await fetchUsers();
      setSelectedUsers(new Set());
      setShowBulkOwnershipTransferModal(false);
    } catch (error: any) {
      toast.error(error.response?.data?.error || error.response?.data?.message || 'Failed to update ownership transfer settings');
    } finally {
      setIsBulkUpdatingOwnershipTransfer(false);
    }
  };

  const handleBulkDeleteUsers = async () => {
    if (selectedUsers.size === 0) return;
    const ids = Array.from(selectedUsers);
    setIsDeletingUsers(true);
    setBulkDeleteProgress({ current: 0, total: ids.length });
    let successCount = 0;
    let errorCount = 0;
    for (let i = 0; i < ids.length; i++) {
      setBulkDeleteProgress({ current: i + 1, total: ids.length });
      try {
        await apiService.deleteUser(ids[i]);
        successCount++;
      } catch (err: any) {
        errorCount++;
        const msg = err.response?.data?.error || err.response?.data?.message || err.message;
        toast.error(`Failed to delete user: ${msg}`);
      }
    }
    setBulkDeleteProgress(null);
    if (successCount > 0) {
      const msg = errorCount > 0
        ? `Deleted ${successCount} user${successCount !== 1 ? 's' : ''}, ${errorCount} failed.`
        : (tUsers('bulk.deleteSuccess', { count: successCount }) || `Successfully deleted ${successCount} user${successCount !== 1 ? 's' : ''}`);
      toast.success(msg);
      await fetchUsers();
      setSelectedUsers(new Set());
      setShowDeleteConfirm(false);
    }
    setIsDeletingUsers(false);
  };
  
  // Handle create user
  const handleCreateUser = async () => {
    // Validate form
    if (!createUserForm.email || !createUserForm.name || !createUserForm.password || !createUserForm.passwordConfirm) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    // Validate passwords match
    if (createUserForm.password !== createUserForm.passwordConfirm) {
      toast.error(t('messages.passwordsNoMatch') || 'Passwords do not match');
      return;
    }
    
    // Validate password - use company policy if available, otherwise use default (min 8 chars)
    if (passwordValidation && !passwordValidation.isValid) {
      toast.error(passwordValidation.errors[0] || t('messages.passwordRequirementsNotMet') || 'Password does not meet requirements');
      return;
    } else if (!passwordValidation && createUserForm.password.length < 8) {
      // Fallback: if validation didn't run for some reason, check minimum length
      toast.error(t('messages.passwordMinLength') || 'Password must be at least 8 characters long');
      return;
    }
    
    // Validate translator language if translator role is selected
    if (createUserForm.role === 'translator' && !createUserForm.translatorLanguage) {
      toast.error('Please select a language for the translator role');
      return;
    }
    
    setIsCreatingUser(true);
    try {
      // Format role: if translator, combine with language (e.g., translator_es)
      const roleToSave = createUserForm.role === 'translator' 
        ? `translator_${createUserForm.translatorLanguage}` 
        : createUserForm.role;
      
      const userData: any = {
        email: createUserForm.email,
        name: createUserForm.name,
        password: createUserForm.password,
        role: roleToSave,
        mode: createUserForm.mode,
      };
      
      if (createUserForm.company_id) {
        userData.company_id = createUserForm.company_id;
      }
      
      await apiService.createUser(userData);
      toast.success('User created successfully');
      setShowCreateUserModal(false);
      // Reset form
      setCreateUserForm({
        email: '',
        name: '',
        password: '',
        passwordConfirm: '',
        role: 'user',
        translatorLanguage: '',
        company_id: '',
        mode: 'full',
      });
      setPasswordPolicy(null);
      setPasswordValidation(null);
      // Clear search field to prevent browser autocomplete
      setSearchTerm('');
      // Refresh users list
      await fetchUsers();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to create user');
    } finally {
      setIsCreatingUser(false);
    }
  };

  return (
    <div className="p-2 sm:p-4 md:p-6">
      {/* Search and Filters */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 w-4 h-4" />
            <input
              type="text"
              name="user-search"
              id="user-search-input"
              autoComplete="off"
              placeholder={tUsers('searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-xs border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-governate-500 focus:border-transparent h-[36px]"
            />
          </div>
        </div>
        <div className="sm:w-48">
          <div className="relative" ref={roleDropdownRef}>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setRoleDropdownOpen(!roleDropdownOpen);
                setStatusDropdownOpen(false);
              }}
              className="w-full text-xs border border-gray-300 dark:border-gray-600 rounded-md px-2 py-2 pr-6 text-left text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-governate-500 h-[36px] flex items-center justify-between"
            >
              <span className="truncate">
                {selectedRoles.length === 0
                  ? tUsers('filters.allRoles')
                  : selectedRoles.length === 1
                  ? selectedRoles[0] === 'sysadmin' ? tUsers('roles.systemAdmin') : selectedRoles[0] === 'admin' ? tUsers('roles.admin') : tUsers('roles.user')
                  : tUsers('filters.selectedCount', { count: selectedRoles.length })}
              </span>
              <ChevronDownIcon className={`w-3 h-3 text-gray-400 transition-transform ${roleDropdownOpen ? 'rotate-180' : ''}`} />
            </button>
            {roleDropdownOpen && (
              <div className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-auto">
                {[
                  { value: 'sysadmin', label: tUsers('roles.systemAdmin') },
                  { value: 'admin', label: tUsers('roles.admin') },
                  { value: 'user', label: tUsers('roles.user') },
                ].map(role => (
                  <label
                    key={role.value}
                    className="flex items-center px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedRoles.includes(role.value)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedRoles([...selectedRoles, role.value]);
                        } else {
                          setSelectedRoles(selectedRoles.filter(r => r !== role.value));
                        }
                      }}
                      className="h-3.5 w-3.5 text-governate-600 focus:ring-governate-500 border-gray-300 rounded"
                      onClick={(e) => e.stopPropagation()}
                    />
                    <span className="ml-2 text-xs text-gray-700 dark:text-gray-300">{role.label}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="sm:w-48">
          <div className="relative" ref={statusDropdownRef}>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setStatusDropdownOpen(!statusDropdownOpen);
                setRoleDropdownOpen(false);
              }}
              className="w-full text-xs border border-gray-300 dark:border-gray-600 rounded-md px-2 py-2 pr-6 text-left text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-governate-500 h-[36px] flex items-center justify-between"
            >
              <span className="truncate">
                {selectedStatuses.length === 0
                  ? tUsers('filters.allStatus')
                  : selectedStatuses.length === 1
                  ? selectedStatuses[0] === 'active' ? tUsers('statuses.active') : selectedStatuses[0] === 'pending-verified' ? tUsers('statuses.pendingVerified') : tUsers('statuses.pendingUnverified')
                  : tUsers('filters.selectedCount', { count: selectedStatuses.length })}
              </span>
              <ChevronDownIcon className={`w-3 h-3 text-gray-400 transition-transform ${statusDropdownOpen ? 'rotate-180' : ''}`} />
            </button>
            {statusDropdownOpen && (
              <div className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-auto">
                {[
                  { value: 'active', label: tUsers('statuses.active') },
                  { value: 'pending-verified', label: tUsers('statuses.pendingVerified') },
                  { value: 'pending-unverified', label: tUsers('statuses.pendingUnverified') },
                ].map(status => (
                  <label
                    key={status.value}
                    className="flex items-center px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedStatuses.includes(status.value)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedStatuses([...selectedStatuses, status.value]);
                        } else {
                          setSelectedStatuses(selectedStatuses.filter(s => s !== status.value));
                        }
                      }}
                      className="h-3.5 w-3.5 text-governate-600 focus:ring-governate-500 border-gray-300 rounded"
                      onClick={(e) => e.stopPropagation()}
                    />
                    <span className="ml-2 text-xs text-gray-700 dark:text-gray-300">{status.label}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bulk Actions Bar - icon layout like Assets */}
      {selectedUsers.size > 0 && (currentUser?.role === 'sysadmin' || currentUser?.role === 'admin') && (
        <div className="bg-white dark:bg-black rounded-lg shadow mb-4 px-4 py-3 border border-gray-200 dark:border-gray-800">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {tUsers('count.usersSelected', { count: selectedUsers.size })}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {/* Activate (only for pending-verified users) */}
              {hasPendingVerifiedSelected && (
                <button
                  onClick={handleActivateSelected}
                  disabled={isActivating}
                  title={tUsers('bulk.activate')}
                  className="p-2 border-2 border-black dark:border-white text-black dark:text-white bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                >
                  {isActivating ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current" />
                  ) : (
                    <CheckIcon className="w-4 h-4" />
                  )}
                </button>
              )}
              {/* Change Role */}
              <button
                onClick={() => setShowChangeRoleModal(true)}
                disabled={isChangingRole || isChangingCompany || isActivating || isDeletingUsers}
                title={tUsers('bulk.changeRole')}
                className="p-2 border-2 border-black dark:border-white text-black dark:text-white bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
              >
                <UserCircleIcon className="w-4 h-4" />
              </button>
              {/* Change Company - sysadmin always, admin only if they manage companies */}
              {(currentUser?.role === 'sysadmin' || (currentUser?.role === 'admin' && hasManagedCompanies)) && (
                <button
                  onClick={() => setShowChangeCompanyModal(true)}
                  disabled={isChangingRole || isChangingCompany || isActivating || isBulkUpdatingOwnershipTransfer || isDeletingUsers}
                  title={tUsers('bulk.changeCompany')}
                  className="p-2 border-2 border-black dark:border-white text-black dark:text-white bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                >
                  <BuildingOfficeIcon className="w-4 h-4" />
                </button>
              )}
              {/* Ownership Transfer */}
              <button
                onClick={() => setShowBulkOwnershipTransferModal(true)}
                disabled={isChangingRole || isChangingCompany || isActivating || isBulkUpdatingOwnershipTransfer || isDeletingUsers}
                title={tUsers('bulk.ownershipTransfer')}
                className="p-2 border-2 border-black dark:border-white text-black dark:text-white bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
              >
                <ArrowsRightLeftIcon className="w-4 h-4" />
              </button>
              {/* Delete */}
              {(currentUser?.role === 'sysadmin' || currentUser?.role === 'admin') && (
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={isDeletingUsers}
                  title={tUsers('bulk.delete')}
                  className="p-2 border-2 border-red-600 text-red-600 bg-white dark:bg-gray-800 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Sorting Controls */}
      <div className="bg-white dark:bg-black rounded-lg shadow mb-4 px-4 py-3 border-b border-gray-200 dark:border-gray-800">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="hidden sm:inline text-xs text-gray-500 dark:text-gray-400">{tUsers('sort.sortBy')}</span>
            <div className="relative" ref={sortDropdownRef}>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setSortDropdownOpen(!sortDropdownOpen);
                  setRoleDropdownOpen(false);
                  setStatusDropdownOpen(false);
                }}
                className="text-xs border border-gray-300 dark:border-gray-600 rounded-md px-2 py-2 pr-6 text-left text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-governate-500 min-w-[140px] h-[36px] flex items-center justify-between"
              >
                <span className="truncate">
                  {sortField === 'email' ? tUsers('sort.email') :
                   sortField === 'role' ? tUsers('sort.role') :
                   sortField === 'company' ? tUsers('sort.company') :
                   sortField === 'status' ? tUsers('sort.status') :
                   sortField === 'created_at' ? tUsers('sort.registration') :
                   sortField === 'last_login' ? tUsers('sort.lastLogin') :
                   sortField === 'file_count' ? tUsers('sort.numberOfAssets') : tUsers('sort.registration')}
                </span>
                <ChevronDownIcon className={`w-3 h-3 text-gray-400 transition-transform flex-shrink-0 ${sortDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              {sortDropdownOpen && (
                <div className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-auto">
                  {[
                    { value: 'email', label: tUsers('sort.email') },
                    { value: 'role', label: tUsers('sort.role') },
                    { value: 'company', label: tUsers('sort.company') },
                    { value: 'status', label: tUsers('sort.status') },
                    { value: 'created_at', label: tUsers('sort.registration') },
                    { value: 'last_login', label: tUsers('sort.lastLogin') },
                    { value: 'file_count', label: tUsers('sort.numberOfAssets') },
                  ].filter(option => option.value !== sortField).map(option => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        const newField = option.value as SortField;
                        setSortField(newField);
                        setSortOrder('asc');
                        setSortDropdownOpen(false);
                      }}
                      className="w-full text-left px-3 py-2 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="p-1.5 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
              title={sortOrder === 'asc' ? tUsers('sort.sortDescending') : tUsers('sort.sortAscending')}
            >
              <ArrowsUpDownIcon className="w-4 h-4" />
            </button>
            {currentUser?.role === 'admin' && (
              <button
                onClick={() => setGroupByCompany(!groupByCompany)}
                className={`text-sm px-3 py-1.5 rounded-md transition-colors ${
                  groupByCompany
                    ? 'bg-governate-100 text-governate-700 dark:bg-governate-900/30 dark:text-governate-300 font-medium'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                }`}
              >
                {tUsers('groupByOrganisation')}
              </button>
            )}
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {tUsers(sortedUsers.length === 1 ? 'count.user' : 'count.users', { count: sortedUsers.length })}
          </div>
        </div>
      </div>

      {/* Users List */}
      <div className="bg-white dark:bg-black rounded-lg shadow overflow-hidden">
        {/* User Count */}
        {!loading && sortedUsers.length > 0 && (
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900">
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {tUsers('count.showing', { count: sortedUsers.length })}
            </span>
          </div>
        )}
        {loading ? (
          <div className="px-6 py-12 text-center bg-white dark:bg-black">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-governate-600"></div>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">{tUsers('loadingUsers')}</p>
          </div>
        ) : sortedUsers.length === 0 ? (
          <div className="px-6 py-12 text-center bg-white dark:bg-black">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">{tUsers('empty.title')}</h3>
            <p className="text-gray-500 dark:text-gray-400">{tUsers('empty.searchHint')}</p>
          </div>
        ) : groupByCompany && currentUser?.role === 'admin' ? (
          <div className="space-y-0">
            {Object.entries(groupedUsers).map(([groupId, group]) => {
              const isExpanded = expandedGroups.has(groupId);
              const isUngrouped = groupId === 'ungrouped';
              
              return (
                <div key={groupId} className="border-b border-gray-200 dark:border-gray-800">
                  {/* Group Header (Organisation) */}
                  {!isUngrouped && (
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleGroup(groupId);
                      }}
                      className="bg-gray-50 dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors px-4 py-3 cursor-pointer flex items-center justify-between"
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        {isExpanded ? (
                          <ChevronDownIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
                        ) : (
                          <ChevronRightIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
                        )}
                        <BuildingOfficeIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                            {group.companyName}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {tUsers(group.users.length === 1 ? 'count.user' : 'count.users', { count: group.users.length })}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Grouped Users */}
                  {isExpanded && (
                    <div className="space-y-0">
                      {group.users.map((user: User) => (
                        <div
                          key={user.id}
                          onClick={() => handleUserClick(user)}
                          className="border-l-4 border-l-transparent bg-white dark:bg-black hover:bg-gray-50 dark:hover:bg-gray-900 hover:border-l-governate-500 transition-all cursor-pointer px-4 py-3 border-b border-gray-200 dark:border-gray-800"
                        >
                          {/* Row 1: Name/Email | Role | Status Dot | Desktop Actions | Mobile Menu */}
                          <div className="flex items-center justify-between gap-2 sm:gap-4">
                            <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                              {/* Checkbox - for sysadmin and admin */}
                              {(currentUser?.role === 'sysadmin' || currentUser?.role === 'admin') && (
                                <input
                                  type="checkbox"
                                  checked={selectedUsers.has(user.id)}
                                  disabled={isSysAdmin && (user.role === 'sysadmin' || (user.role && String(user.role).startsWith('sysadmin')))}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    if (isSysAdmin && (user.role === 'sysadmin' || (user.role && String(user.role).startsWith('sysadmin')))) return;
                                    handleSelectUser(user.id);
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                  className="h-4 w-4 text-governate-600 focus:ring-governate-500 border-gray-300 rounded flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                                />
                              )}
                              {/* Avatar - hidden on small screens */}
                              <div className="hidden sm:flex flex-shrink-0 h-10 w-10">
                                <div className="h-10 w-10 rounded-full bg-governate-100 dark:bg-governate-900/30 flex items-center justify-center">
                                  <span className="text-sm font-medium text-governate-600 dark:text-governate-400">
                                    {getDisplayName(user).charAt(0).toUpperCase()}
                                  </span>
                                </div>
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                                  {getDisplayName(user)}
                                </div>
                                <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 truncate">
                                  {user.email}
                                </div>
                                {/* Row 2: Registered at | Last Login | Company (mobile only) */}
                                <div className="mt-1 sm:hidden flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                                  <span>{tUsers('registeredAt')} {formatDate(user.created_at)}</span>
                                  {user.last_login && <span>• {tUsers('lastLogin')} {formatDate(user.last_login)}</span>}
                                  {(currentUser?.role === 'sysadmin' || currentUser?.role === 'admin') && user.company_name && (
                                    <span>• {user.company_name}</span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
                              <span className={`hidden sm:inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(user.role)}`}>
                                {user.role}
                              </span>
                              <span className="hidden sm:inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                                {user.mode === 'reader' ? tUsers('mode.reader') : tUsers('mode.full')}
                              </span>
                              <div className={`w-2.5 h-2.5 rounded-full ${
                                user.is_active ? 'bg-green-500' : 'bg-yellow-500'
                              }`} />
                              
                              {/* Desktop: Activity button and file count */}
                              <div className="hidden md:flex items-center gap-3">
                                {user.file_count !== undefined && user.file_count !== null && (
                                  <button
                                    onClick={(e) => handleViewUserAssets(e, user)}
                                    className="text-xs text-governate-600 hover:text-governate-700 dark:text-governate-400 dark:hover:text-governate-300 font-medium transition-colors hover:underline"
                                    title={`View ${user.file_count} asset${user.file_count !== 1 ? 's' : ''} owned by this user`}
                                  >
                                    {tUsers(user.file_count === 1 ? 'count.asset' : 'count.assets', { count: user.file_count })}
                                  </button>
                                )}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleViewActivity(user);
                                  }}
                                  className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 transition-colors p-1.5 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20"
                                  title={tUsers('viewActivity')}
                                >
                                  <ClockIcon className="w-5 h-5" />
                                </button>
                              </div>

                              {/* Mobile: Three dots menu */}
                              <div className="md:hidden relative">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenUserMenu(openUserMenu === user.id ? null : user.id);
                                  }}
                                  className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                                  title={tUsers('moreOptions')}
                                >
                                  <EllipsisVerticalIcon className="w-5 h-5" />
                                </button>
                                
                                {/* Dropdown Menu */}
                                {openUserMenu === user.id && (
                                  <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 z-10">
                                    <div className="py-1">
                                      {/* Role & Mode */}
                                      <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-700 flex flex-wrap gap-1.5 items-center">
                                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(user.role)}`}>
                                          {user.role}
                                        </span>
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                                          {user.mode === 'reader' ? tUsers('mode.reader') : tUsers('mode.full')}
                                        </span>
                                      </div>
                                      
                                      {/* Registered at */}
                                      <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-700">
                                        <div className="text-xs text-gray-500 dark:text-gray-400">{tUsers('registeredAt')}</div>
                                        <div className="text-sm text-gray-900 dark:text-gray-100">{formatDate(user.created_at)}</div>
                                      </div>
                                      
                                      {/* Last login */}
                                      {user.last_login && (
                                        <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-700">
                                          <div className="text-xs text-gray-500 dark:text-gray-400">{tUsers('lastLogin')}</div>
                                          <div className="text-sm text-gray-900 dark:text-gray-100">{formatDate(user.last_login)}</div>
                                        </div>
                                      )}
                                      
                                      {/* Number of assets */}
                                      {user.file_count !== undefined && user.file_count !== null && (
                                        <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-700">
                                          <div className="text-xs text-gray-500 dark:text-gray-400">{tUsers('numberOfAssets')}</div>
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleViewUserAssets(e, user);
                                              setOpenUserMenu(null);
                                            }}
                                            className="text-sm text-governate-600 hover:text-governate-700 dark:text-governate-400 dark:hover:text-governate-300 font-medium transition-colors hover:underline"
                                          >
                                            {tUsers(user.file_count === 1 ? 'count.asset' : 'count.assets', { count: user.file_count })}
                                          </button>
                                        </div>
                                      )}
                                      
                                      {/* Company (sysadmin and admin) */}
                                      {(currentUser?.role === 'sysadmin' || currentUser?.role === 'admin') && user.company_name && (
                                        <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-700">
                                          <div className="text-xs text-gray-500 dark:text-gray-400">{tUsers('company')}</div>
                                          <div className="text-sm text-gray-900 dark:text-gray-100">{user.company_name}</div>
                                        </div>
                                      )}
                                      
                                      {/* Activity Logs */}
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleViewActivity(user);
                                          setOpenUserMenu(null);
                                        }}
                                        className="w-full px-4 py-2 text-left text-sm text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20 transition-colors flex items-center gap-2"
                                      >
                                        <ClockIcon className="w-4 h-4" />
                                        <span>{tUsers('activityLogs')}</span>
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Desktop Row 2: Company | Registered at | Last Login | File Count */}
                          <div className="hidden md:flex mt-2 items-center justify-between gap-4 flex-wrap">
                            <div className="flex items-center gap-4 flex-1 min-w-0">
                              {(currentUser?.role === 'sysadmin' || currentUser?.role === 'admin') && user.company_name && (
                                <span className="text-xs text-gray-700 dark:text-gray-300 truncate">{user.company_name}</span>
                              )}
                              <div className="flex items-center gap-1">
                                <span className="text-xs text-gray-500 dark:text-gray-400">{tUsers('registeredAt')}</span>
                                <span className="text-xs text-gray-700 dark:text-gray-300 whitespace-nowrap">{formatDate(user.created_at)}</span>
                              </div>
                              {user.last_login && (
                                <div className="flex items-center gap-1">
                                  <span className="text-xs text-gray-500 dark:text-gray-400">{tUsers('lastLogin')}</span>
                                  <span className="text-xs text-gray-700 dark:text-gray-300 whitespace-nowrap">{formatDate(user.last_login)}</span>
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-4 flex-shrink-0">
                              {user.file_count !== undefined && user.file_count !== null && (
                                <button
                                  onClick={(e) => handleViewUserAssets(e, user)}
                                  className="text-xs text-governate-600 hover:text-governate-700 dark:text-governate-400 dark:hover:text-governate-300 font-medium transition-colors hover:underline"
                                  title={`View ${user.file_count} asset${user.file_count !== 1 ? 's' : ''} owned by this user`}
                                >
                                  {tUsers(user.file_count === 1 ? 'count.asset' : 'count.assets', { count: user.file_count })}
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="space-y-0">
            {sortedUsers.map((user) => (
              <div
                key={user.id}
                onClick={() => handleUserClick(user)}
                className="border-l-4 border-l-transparent bg-white dark:bg-black hover:bg-gray-50 dark:hover:bg-gray-900 hover:border-l-governate-500 transition-all cursor-pointer px-4 py-3 border-b border-gray-200 dark:border-gray-800"
              >
                {/* Row 1: Name/Email | Role | Status Dot | Desktop Actions | Mobile Menu */}
                <div className="flex items-center justify-between gap-2 sm:gap-4">
                  <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                    {/* Checkbox - for sysadmin and admin */}
                    {(currentUser?.role === 'sysadmin' || currentUser?.role === 'admin') && (
                      <input
                        type="checkbox"
                        checked={selectedUsers.has(user.id)}
                        disabled={isSysAdmin && (user.role === 'sysadmin' || (user.role && String(user.role).startsWith('sysadmin')))}
                        onChange={(e) => {
                          e.stopPropagation();
                          if (isSysAdmin && (user.role === 'sysadmin' || (user.role && String(user.role).startsWith('sysadmin')))) return;
                          handleSelectUser(user.id);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        className="h-4 w-4 text-governate-600 focus:ring-governate-500 border-gray-300 rounded flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                      />
                    )}
                    {/* Avatar - hidden on small screens */}
                    <div className="hidden sm:flex flex-shrink-0 h-10 w-10">
                      <div className="h-10 w-10 rounded-full bg-governate-100 dark:bg-governate-900/30 flex items-center justify-center">
                        <span className="text-sm font-medium text-governate-600 dark:text-governate-400">
                          {getDisplayName(user).charAt(0).toUpperCase()}
                        </span>
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                        {getDisplayName(user)}
                      </div>
                      <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 truncate">
                        {user.email}
                      </div>
                      {/* Row 2: Registered at | Last Login | Company (mobile only) */}
                      <div className="mt-1 sm:hidden flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                        <span>registered at: {formatDate(user.created_at)}</span>
                        {user.last_login && <span>• last login: {formatDate(user.last_login)}</span>}
                        {(currentUser?.role === 'sysadmin' || currentUser?.role === 'admin') && user.company_name && (
                          <span>• {user.company_name}</span>
                        )}
                        {user.file_count !== undefined && user.file_count !== null && (
                          <span>• {tUsers(user.file_count === 1 ? 'count.asset' : 'count.assets', { count: user.file_count })}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
                    <span className={`hidden sm:inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(user.role)}`}>
                      {user.role}
                    </span>
                    <span className="hidden sm:inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                      {user.mode === 'reader' ? tUsers('mode.reader') : tUsers('mode.full')}
                    </span>
                    <div className={`w-2.5 h-2.5 rounded-full ${
                      user.is_active ? 'bg-green-500' : 'bg-yellow-500'
                    }`} />
                    
                    {/* Desktop: Activity button and file count */}
                    <div className="hidden md:flex items-center gap-3">
                      {user.file_count !== undefined && user.file_count !== null && (
                        <button
                          onClick={(e) => handleViewUserAssets(e, user)}
                          className="text-xs text-governate-600 hover:text-governate-700 dark:text-governate-400 dark:hover:text-governate-300 font-medium transition-colors hover:underline"
                          title={`View ${user.file_count} asset${user.file_count !== 1 ? 's' : ''} owned by this user`}
                        >
                          {tUsers(user.file_count === 1 ? 'count.asset' : 'count.assets', { count: user.file_count })}
                        </button>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewActivity(user);
                        }}
                        className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 transition-colors p-1.5 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20"
                        title={tUsers('viewActivity')}
                      >
                        <ClockIcon className="w-5 h-5" />
                      </button>
                    </div>

                    {/* Mobile: Three dots menu */}
                    <div className="md:hidden relative">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenUserMenu(openUserMenu === user.id ? null : user.id);
                        }}
                        className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                        title={tUsers('moreOptions')}
                      >
                        <EllipsisVerticalIcon className="w-5 h-5" />
                      </button>
                      
                      {/* Dropdown Menu */}
                      {openUserMenu === user.id && (
                        <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 z-10">
                          <div className="py-1">
                            {/* Role & Mode */}
                            <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-700 flex flex-wrap gap-1.5 items-center">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(user.role)}`}>
                                {user.role}
                              </span>
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                                {user.mode === 'reader' ? tUsers('mode.reader') : tUsers('mode.full')}
                              </span>
                            </div>
                            
                            {/* Registered at */}
                            <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-700">
                              <div className="text-xs text-gray-500 dark:text-gray-400">{tUsers('registeredAt')}</div>
                              <div className="text-sm text-gray-900 dark:text-gray-100">{formatDate(user.created_at)}</div>
                            </div>
                            
                            {/* Last login */}
                            {user.last_login && (
                              <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-700">
                                <div className="text-xs text-gray-500 dark:text-gray-400">{tUsers('lastLogin')}</div>
                                <div className="text-sm text-gray-900 dark:text-gray-100">{formatDate(user.last_login)}</div>
                              </div>
                            )}
                            
                            {/* Number of assets */}
                            {user.file_count !== undefined && user.file_count !== null && (
                              <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-700">
                                <div className="text-xs text-gray-500 dark:text-gray-400">{tUsers('numberOfAssets')}</div>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleViewUserAssets(e, user);
                                    setOpenUserMenu(null);
                                  }}
                                  className="text-sm text-governate-600 hover:text-governate-700 dark:text-governate-400 dark:hover:text-governate-300 font-medium transition-colors hover:underline"
                                >
                                  {tUsers(user.file_count === 1 ? 'count.asset' : 'count.assets', { count: user.file_count })}
                                </button>
                              </div>
                            )}
                            
                            {/* Company (sysadmin and admin) */}
                            {(currentUser?.role === 'sysadmin' || currentUser?.role === 'admin') && user.company_name && (
                              <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-700">
                                <div className="text-xs text-gray-500 dark:text-gray-400">{tUsers('company')}</div>
                                <div className="text-sm text-gray-900 dark:text-gray-100">{user.company_name}</div>
                              </div>
                            )}
                            
                            {/* Activity Logs */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewActivity(user);
                                setOpenUserMenu(null);
                              }}
                              className="w-full px-4 py-2 text-left text-sm text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20 transition-colors flex items-center gap-2"
                            >
                              <ClockIcon className="w-4 h-4" />
                              <span>{tUsers('activityLogs')}</span>
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Desktop Row 2: Company | Registered at | Last Login | File Count */}
                <div className="hidden md:flex mt-2 items-center justify-between gap-4 flex-wrap">
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    {(currentUser?.role === 'sysadmin' || currentUser?.role === 'admin') && user.company_name && (
                      <span className="text-xs text-gray-700 dark:text-gray-300 truncate">{user.company_name}</span>
                    )}
                    <div className="flex items-center gap-1">
                      <span className="text-xs text-gray-500 dark:text-gray-400">{tUsers('registeredAt')}</span>
                      <span className="text-xs text-gray-700 dark:text-gray-300 whitespace-nowrap">{formatDate(user.created_at)}</span>
                    </div>
                    {user.last_login && (
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-gray-500 dark:text-gray-400">{tUsers('lastLogin')}</span>
                        <span className="text-xs text-gray-700 dark:text-gray-300 whitespace-nowrap">{formatDate(user.last_login)}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-4 flex-shrink-0">
                    {user.file_count !== undefined && user.file_count !== null && (
                      <button
                        onClick={(e) => handleViewUserAssets(e, user)}
                        className="text-xs text-governate-600 hover:text-governate-700 dark:text-governate-400 dark:hover:text-governate-300 font-medium transition-colors hover:underline"
                        title={`View ${user.file_count} asset${user.file_count !== 1 ? 's' : ''} owned by this user`}
                      >
                        {tUsers(user.file_count === 1 ? 'count.asset' : 'count.assets', { count: user.file_count })}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Change Role Modal */}
      {showChangeRoleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Change Role for {selectedUsers.size} User{selectedUsers.size !== 1 ? 's' : ''}
              </h3>
            </div>
            <div className="px-6 py-4">
              <div className="space-y-2">
                {[
                  { value: 'user', label: 'User' },
                  { value: 'admin', label: 'Admin' },
                  ...(currentUser?.role === 'sysadmin' ? [{ value: 'sysadmin', label: 'System Admin' }] : []),
                ].map(role => (
                  <button
                    key={role.value}
                    onClick={() => handleBulkChangeRole(role.value as 'user' | 'admin' | 'sysadmin')}
                    disabled={isChangingRole}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {role.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
              <button
                onClick={() => setShowChangeRoleModal(false)}
                disabled={isChangingRole}
                className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Change Company Modal */}
      {showChangeCompanyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[80vh] flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Change Company for {selectedUsers.size} User{selectedUsers.size !== 1 ? 's' : ''}
              </h3>
            </div>
            <div className="px-6 py-4 overflow-y-auto flex-1">
              <div className="space-y-2">
                <button
                  onClick={() => handleBulkChangeCompany(null)}
                  disabled={isChangingCompany}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {tUsers('noClient')}
                </button>
                {(currentUser?.role === 'sysadmin' ? companies : managedCompanies).map(company => (
                  <button
                    key={company.id}
                    onClick={() => handleBulkChangeCompany(company.id)}
                    disabled={isChangingCompany}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {company.name}
                  </button>
                ))}
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
              <button
                onClick={() => setShowChangeCompanyModal(false)}
                disabled={isChangingCompany}
                className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Ownership Transfer Modal */}
      {showBulkOwnershipTransferModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Ownership Transfer for {selectedUsers.size} User{selectedUsers.size !== 1 ? 's' : ''}
              </h3>
            </div>
            <div className="px-6 py-4">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Choose whether selected users can transfer ownership of assets to other users.
              </p>
              <div className="space-y-2">
                <button
                  onClick={() => handleBulkUpdateOwnershipTransfer(true)}
                  disabled={isBulkUpdatingOwnershipTransfer}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Enable Ownership Transfer
                </button>
                <button
                  onClick={() => handleBulkUpdateOwnershipTransfer(false)}
                  disabled={isBulkUpdatingOwnershipTransfer}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Disable Ownership Transfer
                </button>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
              <button
                onClick={() => setShowBulkOwnershipTransferModal(false)}
                disabled={isBulkUpdatingOwnershipTransfer}
                className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
          onClick={() => !isDeletingUsers && setShowDeleteConfirm(false)}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 border-2 border-red-600 rounded-full flex items-center justify-center flex-shrink-0">
                <TrashIcon className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {tUsers('bulk.deleteTitle')}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {tUsers('bulk.deleteSubtitle')}
                </p>
              </div>
            </div>
            <div className="mb-6">
              {isDeletingUsers && bulkDeleteProgress ? (
                <>
                  <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">
                    {tUsers('bulk.deletingProgress', { current: bulkDeleteProgress.current, total: bulkDeleteProgress.total })}
                  </p>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5 overflow-hidden">
                    <div
                      className="h-full bg-red-600 transition-all duration-300 ease-out"
                      style={{ width: `${(bulkDeleteProgress.current / bulkDeleteProgress.total) * 100}%` }}
                    />
                  </div>
                </>
              ) : (
                <>
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    {tUsers('bulk.deleteConfirm', { count: selectedUsers.size })}
                  </p>
                  <p className="text-sm text-amber-700 dark:text-amber-300 mt-2">
                    {tUsers('bulk.deleteCompanyUserNote')}
                  </p>
                  <div className="mt-3 p-3 bg-white dark:bg-gray-700 border border-red-200 dark:border-red-900 rounded-lg">
                    <p className="text-sm text-red-800 dark:text-red-200 font-medium">⚠️ {tUsers('bulk.deleteWarning')}</p>
                    <ul className="text-sm text-red-700 dark:text-red-300 mt-1 ml-4 list-disc">
                      <li>{tUsers('bulk.deleteWarning1')}</li>
                      <li>{tUsers('bulk.deleteWarning2')}</li>
                    </ul>
                  </div>
                </>
              )}
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => !isDeletingUsers && setShowDeleteConfirm(false)}
                disabled={isDeletingUsers}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors"
              >
                {tUsers('bulk.cancel')}
              </button>
              <button
                onClick={handleBulkDeleteUsers}
                disabled={isDeletingUsers}
                className="flex-1 px-4 py-2 bg-white dark:bg-gray-800 text-red-600 border-2 border-red-600 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
              >
                {isDeletingUsers ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600" />
                    <span>{tUsers('bulk.deleting')}</span>
                  </>
                ) : (
                  <>
                    <TrashIcon className="w-4 h-4" />
                    <span>{tUsers('bulk.delete')}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Create User Modal - Sysadmin only */}
      {showCreateUserModal && isSysAdmin && createPortal(
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div 
              className="fixed inset-0 transition-opacity bg-gray-500 dark:bg-gray-900 bg-opacity-75 dark:bg-opacity-75"
              onClick={() => !isCreatingUser && setShowCreateUserModal(false)}
            ></div>
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
              <form
                key={`create-user-form-${createUserFormKey}`}
                onSubmit={(e) => {
                  e.preventDefault();
                  handleCreateUser();
                }}
                autoComplete="off"
                noValidate
              >
                <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100">
                      Create New User
                    </h3>
                    <button
                      type="button"
                      onClick={() => !isCreatingUser && setShowCreateUserModal(false)}
                      disabled={isCreatingUser}
                      className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                    >
                      <XMarkIcon className="w-6 h-6" />
                    </button>
                  </div>
                  
                  <div className="space-y-4">
                  {/* Hidden dummy fields to confuse browser autocomplete */}
                  <div style={{ position: 'absolute', left: '-9999px', opacity: 0, pointerEvents: 'none' }}>
                    <input type="email" name="fake-email" autoComplete="off" tabIndex={-1} />
                    <input type="password" name="fake-password" autoComplete="off" tabIndex={-1} />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Company (Optional)
                    </label>
                    <select
                      value={createUserForm.company_id}
                      onChange={(e) => setCreateUserForm({ ...createUserForm, company_id: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-governate-500 focus:border-governate-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    >
                      <option value="">No Company (individual account)</option>
                      {createUserCompaniesWithLicenses.map((company) => (
                        <option key={company.id} value={company.id}>
                          {company.name}
                        </option>
                      ))}
                    </select>
                    {selectedCompanyAvailability && (
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        Available: {selectedCompanyAvailability.full_available} Full, {selectedCompanyAvailability.reader_available} Reader
                      </p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name={`new-user-email-${createUserFormKey}`}
                      id={`new-user-email-${createUserFormKey}`}
                      autoComplete="off"
                      data-lpignore="true"
                      data-form-type="other"
                      value={createUserForm.email}
                      onChange={(e) => setCreateUserForm({ ...createUserForm, email: e.target.value })}
                      onFocus={(e) => {
                        // Clear any browser autofill on focus
                        if (e.target.value && !createUserForm.email) {
                          e.target.value = '';
                          setCreateUserForm({ ...createUserForm, email: '' });
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-governate-500 focus:border-governate-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      placeholder="user@example.com"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name={`new-user-name-${createUserFormKey}`}
                      id={`new-user-name-${createUserFormKey}`}
                      autoComplete="off"
                      data-lpignore="true"
                      value={createUserForm.name}
                      onChange={(e) => setCreateUserForm({ ...createUserForm, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-governate-500 focus:border-governate-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      placeholder="John Doe"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Password <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="password"
                      name={`new-user-password-${createUserFormKey}`}
                      id={`new-user-password-${createUserFormKey}`}
                      autoComplete="new-password"
                      data-lpignore="true"
                      data-form-type="other"
                      value={createUserForm.password}
                      onChange={(e) => setCreateUserForm({ ...createUserForm, password: e.target.value })}
                      onFocus={(e) => {
                        // Clear any browser autofill on focus
                        if (e.target.value && !createUserForm.password) {
                          e.target.value = '';
                          setCreateUserForm({ ...createUserForm, password: '' });
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-governate-500 focus:border-governate-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      placeholder={passwordPolicy ? `Enter password (minimum ${passwordPolicy.min_length} characters)` : "Minimum 8 characters"}
                    />
                    {!isLoadingPasswordPolicy && createUserForm.password.length > 0 && passwordValidation && (
                      <div className="mt-2 space-y-1">
                        {passwordValidation.checks.minLength.message && (
                          <div className={`text-xs ${passwordValidation.checks.minLength.valid ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                            {passwordValidation.checks.minLength.message}
                          </div>
                        )}
                        {passwordPolicy?.require_uppercase && passwordValidation.checks.uppercase.message && (
                          <div className={`text-xs ${passwordValidation.checks.uppercase.valid ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                            {passwordValidation.checks.uppercase.message}
                          </div>
                        )}
                        {passwordPolicy?.require_lowercase && passwordValidation.checks.lowercase.message && (
                          <div className={`text-xs ${passwordValidation.checks.lowercase.valid ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                            {passwordValidation.checks.lowercase.message}
                          </div>
                        )}
                        {passwordPolicy?.require_numbers && passwordValidation.checks.numbers.message && (
                          <div className={`text-xs ${passwordValidation.checks.numbers.valid ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                            {passwordValidation.checks.numbers.message}
                          </div>
                        )}
                        {passwordPolicy?.require_special_chars && passwordValidation.checks.specialChars.message && (
                          <div className={`text-xs ${passwordValidation.checks.specialChars.valid ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                            {passwordValidation.checks.specialChars.message}
                          </div>
                        )}
                        {passwordPolicy?.prohibit_sequential_numbers && passwordValidation.checks.sequentialNumbers.message && (
                          <div className={`text-xs ${passwordValidation.checks.sequentialNumbers.valid ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                            {passwordValidation.checks.sequentialNumbers.message}
                          </div>
                        )}
                        {passwordPolicy?.prohibit_repeated_characters && passwordValidation.checks.repeatedChars.message && (
                          <div className={`text-xs ${passwordValidation.checks.repeatedChars.valid ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                            {passwordValidation.checks.repeatedChars.message}
                          </div>
                        )}
                        {passwordPolicy?.prohibit_user_info && passwordValidation.checks.userInfo.message && (
                          <div className={`text-xs ${passwordValidation.checks.userInfo.valid ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                            {passwordValidation.checks.userInfo.message}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Confirm Password <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="password"
                      name={`new-user-password-confirm-${createUserFormKey}`}
                      id={`new-user-password-confirm-${createUserFormKey}`}
                      autoComplete="new-password"
                      data-lpignore="true"
                      data-form-type="other"
                      value={createUserForm.passwordConfirm}
                      onChange={(e) => setCreateUserForm({ ...createUserForm, passwordConfirm: e.target.value })}
                      onFocus={(e) => {
                        // Clear any browser autofill on focus
                        if (e.target.value && !createUserForm.passwordConfirm) {
                          e.target.value = '';
                          setCreateUserForm({ ...createUserForm, passwordConfirm: '' });
                        }
                      }}
                      className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-governate-500 focus:border-governate-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 ${
                        createUserForm.passwordConfirm && createUserForm.password !== createUserForm.passwordConfirm
                          ? 'border-red-300 dark:border-red-600'
                          : 'border-gray-300 dark:border-gray-600'
                      }`}
                      placeholder="Confirm password"
                    />
                    {createUserForm.passwordConfirm && createUserForm.password !== createUserForm.passwordConfirm && (
                      <div className="mt-1 text-xs text-red-600 dark:text-red-400">
                        {t('messages.passwordsNoMatch') || 'Passwords do not match'}
                      </div>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Mode
                    </label>
                    {createUserForm.company_id ? (
                      companyHasBothModes ? (
                        <select
                          value={createUserForm.mode}
                          onChange={(e) => setCreateUserForm({ ...createUserForm, mode: e.target.value as 'full' | 'reader' })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-governate-500 focus:border-governate-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        >
                          <option value="full">Full</option>
                          <option value="reader">Reader</option>
                        </select>
                      ) : (
                        <div className="px-3 py-2 rounded-md text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600">
                          {companyOnlyFull ? tUsers('mode.full') : tUsers('mode.reader')}
                        </div>
                      )
                    ) : (
                      <select
                        value={createUserForm.mode}
                        onChange={(e) => setCreateUserForm({ ...createUserForm, mode: e.target.value as 'full' | 'reader' })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-governate-500 focus:border-governate-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      >
                        <option value="full">Full</option>
                        <option value="reader">Reader</option>
                      </select>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Role <span className="text-red-500">*</span>
                    </label>
                    <div className="space-y-2">
                      <select
                        value={createUserForm.role}
                        onChange={(e) => {
                          const newRole = e.target.value as any;
                          setCreateUserForm({ 
                            ...createUserForm, 
                            role: newRole,
                            translatorLanguage: newRole === 'translator' ? createUserForm.translatorLanguage || 'en' : ''
                          });
                        }}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-governate-500 focus:border-governate-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      >
                        <option value="user">User</option>
                        <option value="admin">Admin</option>
                        <option value="sysadmin">Sysadmin</option>
                        <option value="translator">Translator</option>
                      </select>
                      {createUserForm.role === 'translator' && (
                        <select
                          value={createUserForm.translatorLanguage}
                          onChange={(e) => setCreateUserForm({ ...createUserForm, translatorLanguage: e.target.value as any })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-governate-500 focus:border-governate-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        >
                          <option value="">Select Language</option>
                          <option value="en">English</option>
                          <option value="de">Deutsch</option>
                          <option value="es">Español</option>
                          <option value="fr">Français</option>
                          <option value="fi">Suomi</option>
                          <option value="tr">Türkçe</option>
                        </select>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-gray-50 dark:bg-gray-900 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-3">
                <button
                  type="submit"
                  disabled={isCreatingUser}
                  className="w-full inline-flex justify-center items-center rounded-lg border border-transparent shadow-sm px-4 py-2 bg-governate-600 text-base font-medium text-white hover:bg-governate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-governate-500 disabled:opacity-50 disabled:cursor-not-allowed sm:ml-3 sm:w-auto sm:text-sm"
                >
                  {isCreatingUser ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      Creating...
                    </>
                  ) : (
                    'Create User'
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateUserModal(false)}
                  disabled={isCreatingUser}
                  className="mt-3 w-full inline-flex justify-center rounded-lg border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-governate-500 disabled:opacity-50 disabled:cursor-not-allowed sm:mt-0 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
              </form>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default Users;